<template>
  <div class="auth">
    <div class="card auth__card">
      <div v-if="step === 'probe'" class="row">
        <span class="spinner" aria-hidden="true"></span>
        <span class="muted small">{{ t("common.loading") }}</span>
      </div>

      <template v-else-if="step === 'closed'">
        <h1 class="auth__title">{{ t("auth.inviteOnlyTitle") }}</h1>
        <p class="muted small">{{ t("auth.inviteOnlyBody") }}</p>
        <p class="muted small">{{ t("auth.inviteOnlyHint") }}</p>
      </template>

      <template v-else>
        <h1 class="auth__title">
          {{ step === "verify" ? t("auth.verifyTitle") : step === "provider" ? t("auth.providerStepTitle") : t("auth.signUpTitle") }}
        </h1>
        <p class="auth__lede muted small">
          {{ step === "verify" ? t("auth.otpSent", { email }) : step === "provider" ? t("auth.providerStepLede") : t("auth.signUpLede") }}
        </p>

        <div v-if="error" class="notice notice--danger auth__error">
          <div class="notice__body">
            {{ error.text }}
            <ul v-if="error.list && error.list.length"><li v-for="(item, i) in error.list" :key="i">{{ item }}</li></ul>
          </div>
        </div>

        <form v-if="step === 'form'" class="stack" @submit.prevent="submitForm">
          <div class="field">
            <label class="field__label" for="name">{{ t("auth.yourName") }}</label>
            <input id="name" v-model.trim="name" class="input" autocomplete="name" maxlength="120" required />
          </div>
          <div class="field">
            <label class="field__label" for="email">{{ t("auth.email") }}</label>
            <input id="email" v-model.trim="email" class="input" type="email" autocomplete="username" required />
          </div>
          <div class="field">
            <label class="field__label" for="password">{{ t("auth.password") }}</label>
            <input id="password" v-model="password" class="input" type="password" autocomplete="new-password" minlength="12" required />
            <span class="field__hint">{{ t("auth.passwordHint") }}</span>
          </div>
          <div class="divider"></div>
          <ProviderFields v-model:organisation-name="organisationName" v-model:support-email="supportEmail" />
          <button class="btn btn--block" type="submit" :disabled="busy">
            <span v-if="busy" class="spinner" aria-hidden="true"></span>
            {{ busy ? t("common.loading") : t("nav.signUp") }}
          </button>
        </form>

        <form v-else-if="step === 'verify'" class="stack" @submit.prevent="submitOtp">
          <div class="field">
            <label class="field__label" for="otp">{{ t("auth.otp") }}</label>
            <input id="otp" v-model.trim="otp" class="input input--mono" inputmode="numeric"
                   autocomplete="one-time-code" maxlength="6" required autofocus />
          </div>
          <button class="btn btn--block" type="submit" :disabled="busy || otp.length < 6">
            <span v-if="busy" class="spinner" aria-hidden="true"></span>
            {{ t("auth.verifyAction") }}
          </button>
          <p class="small muted">
            <button type="button" class="auth__switch" :disabled="busy" @click="resend">{{ t("auth.resend") }}</button>
          </p>
        </form>

        <form v-else class="stack" @submit.prevent="submitProvider">
          <ProviderFields v-model:organisation-name="organisationName" v-model:support-email="supportEmail" />
          <button class="btn btn--block" type="submit" :disabled="busy">
            <span v-if="busy" class="spinner" aria-hidden="true"></span>
            {{ t("auth.registerProvider") }}
          </button>
        </form>
      </template>

      <p v-if="step !== 'provider'" class="auth__foot small muted">
        {{ t("auth.haveAccount") }}
        <router-link to="/sign-in">{{ t("nav.signIn") }}</router-link>
      </p>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { api, authApi } from "../lib/api";
import { session, signInWithPassword, verifyEmailOtp, refreshMe } from "../lib/session";
import { useErrorText } from "../lib/errors";
import ProviderFields from "../components/ProviderFields.vue";

const { t } = useI18n();
const errorText = useErrorText();
const router = useRouter();

const step = ref("probe");
const name = ref("");
const email = ref("");
const password = ref("");
const organisationName = ref("");
const supportEmail = ref("");
const otp = ref("");
const busy = ref(false);
const error = ref(null);

onMounted(async () => {
  if (session.token && session.user && !session.developer) {
    step.value = "provider";
    return;
  }
  try {
    const { mode } = (await api.signupMode()) || {};
    if (mode) session.signupMode = mode;
    step.value = mode && mode !== "open" ? "closed" : "form";
  } catch (e) {
    // Hub without the probe: the 403 SIGNUP_CLOSED on submit is the signal instead.
    step.value = "form";
  }
});

async function run(fn) {
  busy.value = true;
  error.value = null;
  try {
    await fn();
  } catch (e) {
    if (e.code === "SIGNUP_CLOSED") {
      session.signupMode = "invite";
      step.value = "closed";
      return;
    }
    error.value = { text: errorText(e), list: e.errors };
  } finally {
    busy.value = false;
  }
}

const submitForm = () => run(async () => {
  await authApi.signUpEmail({ email: email.value, password: password.value, name: name.value });
  await authApi.sendVerificationOtp(email.value);
  step.value = "verify";
});

const resend = () => run(() => authApi.sendVerificationOtp(email.value));

const submitOtp = () => run(async () => {
  const signedIn = await verifyEmailOtp(email.value, otp.value);
  if (!signedIn) {
    const outcome = await signInWithPassword(email.value, password.value);
    if (outcome !== "signed_in") {
      router.push({ name: "sign-in", state: { email: email.value } });
      return;
    }
  }
  password.value = "";
  await registerAndEnter();
});

const submitProvider = () => run(registerAndEnter);

async function registerAndEnter() {
  await api.registerProvider({
    organisation_name: organisationName.value,
    support_email: supportEmail.value
  });
  await refreshMe();
  router.push({ name: "apps" });
}
</script>
